import { Component } from 'react'
import { Box, Table, PrivateImage, TableStructure, SmallTagCounter } from 'stylewhere/components'
import { DecodedItem, TmrProduct, ShippingParcelChecklist, GroupedItemsByProduct } from 'stylewhere/api'
import { AppStore, OperationConfig, OperationReadingProvider } from 'stylewhere/shared'
import { T, __ } from 'stylewhere/i18n'
import { getProductCode, hasChecklist } from 'stylewhere/utils'

interface Props {
  readings: DecodedItem<string>[]
  operation: OperationConfig
  extension: any
  checklist?: ShippingParcelChecklist | null
  products?: { [upc: string]: TmrProduct }
  productKey?: string
  loading?: boolean
}

export class ShippingProductTable extends Component<Props> {
  getProductLabel = (product: TmrProduct): string => {
    const labelArr: string[] = []
    if (product) {
      ;['style', 'material', 'color', 'size'].forEach((key) => {
        let value = '---'
        if (product[key]) {
          value =
            product[key]?.description && product[key].description != null
              ? product[key]?.description
              : product[key]?.value
                ? product[key]?.value
                : '---'
        }
        labelArr.push(value)
      })
    }
    const label = labelArr.join(' / ')
    return label !== '--- / --- / --- / ---' ? label : '---'
  }

  getGroupedProducts = (): Array<GroupedItemsByProduct & { id: string }> => {
    const { readings, operation, extension, checklist, products, productKey } = this.props

    const rows = OperationReadingProvider.groupItems(
      readings,
      operation.readingsDisplayMode === 'groupedByProduct',
      operation,
      extension,
      checklist,
      products,
      true,
      productKey
    ) as GroupedItemsByProduct[]

    // Aggiungi id a ogni item per la Table
    return rows.map((item) => {
      const product = item.item ? item.item.product : item.product
      const productCode = product?.code || product?.id || 'unknown'
      return {
        ...item,
        id: productCode
      }
    })
  }

  getProductTableStructure = (totalQuantity: number): TableStructure<GroupedItemsByProduct & { id: string }>[] => {
    const { operation } = this.props
    const showImages = AppStore.getShowProductImage()
    const structure: TableStructure<GroupedItemsByProduct & { id: string }>[] = []

    if (showImages) {
      structure.push({
        key: 'image',
        label: '',
        width: 160,
        customRender: (item: GroupedItemsByProduct & { id: string }) => {
          const product = item.item ? item.item.product : item.product
          return (
            <Box center p={10}>
              <PrivateImage product={product} width={80} height={80} />
            </Box>
          )
        }
      })
    }

    return [
      ...structure,
      {
        key: 'upc',
        label: 'UPC',
        width: 250,
        customRender: (item: GroupedItemsByProduct & { id: string }) => {
          const product = item.item ? item.item.product : item.product
          return getProductCode(product)
        }
      },
      {
        key: 'modelPartColorSize',
        label: `${__(T.misc.style)} / ${__(T.misc.material)} / ${__(T.misc.color)} / ${__(T.misc.size)}`,
        flex: 1,
        customRender: (item: GroupedItemsByProduct & { id: string }) => {
          const product = item.item ? item.item.product : item.product
          return this.getProductLabel(product)
        }
      },
      {
        key: 'quantity',
        label: `${__(T.misc.quantity, { quantity: totalQuantity })}`,
        width: 240,
        align: 'center',
        customRender: (item: GroupedItemsByProduct & { id: string }) => {
          return (
            <Box center>
              <SmallTagCounter
                detected={item.detected ?? 0}
                expected={hasChecklist(operation) ? (item.expected ?? 0) : undefined}
                unexpected={item.unexpected ?? 0}
                alignSelf={'center'}
              />
            </Box>
          )
        }
      }
    ]
  }

  render() {
    const { loading } = this.props
    const groupedProducts = this.getGroupedProducts()
    const totalQuantity = groupedProducts.reduce((sum, item) => sum + (item.expected ?? 0), 0)

    return (
      <Table
        loading={loading}
        data={groupedProducts}
        structure={this.getProductTableStructure(totalQuantity)}
        showHeader={true}
      />
    )
  }
}
